import { Badge } from "@/components/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Gift, Clock, CheckCircle, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export function RedemptionHistory({ redemptions = [] }) {
  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        label: 'Pendiente',
        icon: Clock,
        className: 'bg-orange-100 text-orange-700 border-orange-200',
        iconColor: 'text-orange-500'
      },
      approved: {
        label: 'Aprobada',
        icon: CheckCircle,
        className: 'bg-green-100 text-green-700 border-green-200',
        iconColor: 'text-green-500'
      },
      fulfilled: {
        label: 'Cumplida',
        icon: Gift,
        className: 'bg-blue-100 text-blue-700 border-blue-200',
        iconColor: 'text-blue-500'
      },
      rejected: {
        label: 'Rechazada',
        icon: XCircle,
        className: 'bg-red-100 text-red-700 border-red-200',
        iconColor: 'text-red-500'
      },
      cancelled: {
        label: 'Cancelada',
        icon: XCircle,
        className: 'bg-gray-100 text-gray-700 border-gray-200',
        iconColor: 'text-gray-500'
      }
    };
    return configs[status] || configs.pending;
  };

  const getCategoryBadge = (category) => {
    const configs = {
      cash: { label: '💰 Saldo', className: 'bg-green-50 text-green-700 border-green-200' },
      experiences: { label: '🎉 Experiencias', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      charity: { label: '❤️ Caridad', className: 'bg-red-50 text-red-700 border-red-200' }
    };
    const config = configs[category] || { label: category, className: 'bg-gray-50 text-gray-700 border-gray-200' };
    return <Badge className={cn("text-xs border", config.className)}>{config.label}</Badge>;
  };

  if (redemptions.length === 0) {
    return (
      <Card className="border-gray-100 shadow-sm">
        <CardContent className="p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Gift className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No hay canjes aún</h3>
            <p className="text-gray-500 mt-1 max-w-sm">
              Cuando canjees tus puntos por recompensas, aparecerán aquí.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Gift className="w-5 h-5 text-green-600" />
          Historial de Canjes
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Revisa el estado de tus solicitudes de canje de puntos.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {redemptions.map((redemption) => {
            const statusConfig = getStatusConfig(redemption.status);
            const StatusIcon = statusConfig.icon;

            return (
              <div
                key={redemption.id}
                className="border border-gray-100 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Left Section: Status Icon + Details */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className={cn("p-3 rounded-lg", statusConfig.className.replace('text-', 'bg-').replace('-700', '-100'))}>
                      <StatusIcon className={cn("w-6 h-6", statusConfig.iconColor)} />
                    </div>

                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-gray-900">
                          {redemption.fulfillment_details || 'Solicitud de canje'}
                        </h4>
                        {getCategoryBadge(redemption.category)}
                      </div>

                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="font-mono font-bold text-orange-600">
                          {redemption.points_spent} pts
                        </span>
                        <span className="text-gray-400">•</span>
                        <span>
                          {new Date(redemption.created_at).toLocaleDateString('es-MX', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>

                      {/* Additional Info */}
                      {redemption.fulfilled_by_user && (
                        <p className="text-xs text-gray-500">
                          Procesado por: {redemption.fulfilled_by_user.full_name}
                          {redemption.fulfilled_at && (
                            <> • {new Date(redemption.fulfilled_at).toLocaleDateString('es-MX')}</>
                          )}
                        </p>
                      )}

                      {redemption.cancellation_reason && (
                        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1 inline-block">
                          Razón: {redemption.cancellation_reason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right Section: Status Badge */}
                  <div className="flex items-center md:justify-end">
                    <Badge className={cn("text-xs border", statusConfig.className)}>
                      {statusConfig.label}
                    </Badge>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
